import type { i18n as I18nInstance } from 'i18next';

import {
  documentHeadByCountry,
  documentHeadByLanguage,
  type DocumentHeadLanguage,
} from '@shared/consts/documentHead.ts';
import {
  AppLanguageLocales,
  resolveUserCountryCode,
  UserCountryCodes,
  type UserCountryCode,
} from '@shared/types/countryCodes.ts';

interface WebsiteJsonLinkedData {
  url: string;
  name: string;
}

const BRAND_NAME_PLACEHOLDER = '{brandName}';

let appliedLanguage: DocumentHeadLanguage = AppLanguageLocales.IT;
let appliedCountryCode: UserCountryCode = UserCountryCodes.IT;

function isDocumentHeadLanguage(language: string): language is DocumentHeadLanguage {
  return Object.hasOwn(documentHeadByLanguage, language);
}

function isWebsiteJsonLinkedData(value: unknown): value is WebsiteJsonLinkedData {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  return 'url' in value && 'name' in value;
}

function setMetaContentByName(name: string, content: string): void {
  const metaElement = document.querySelector(`meta[name="${name}"]`);

  if (metaElement instanceof HTMLMetaElement) {
    metaElement.content = content;
  }
}

function setMetaContentByProperty(property: string, content: string): void {
  const metaElement = document.querySelector(`meta[property="${property}"]`);

  if (metaElement instanceof HTMLMetaElement) {
    metaElement.content = content;
  }
}

function setLinkHrefByRelationship(relationship: string, href: string): void {
  const linkElement = document.querySelector(`link[rel="${relationship}"]`);

  if (linkElement instanceof HTMLLinkElement) {
    linkElement.href = href;
  }
}

function applyJsonLinkedData(url: string, name: string): void {
  const scriptElement = document.querySelector('script[type="application/ld+json"]');

  if (!(scriptElement instanceof HTMLScriptElement) || !scriptElement.textContent) {
    return;
  }

  try {
    const parsedJsonLinkedData: unknown = JSON.parse(scriptElement.textContent);

    if (!isWebsiteJsonLinkedData(parsedJsonLinkedData)) {
      return;
    }

    parsedJsonLinkedData.url = url;
    parsedJsonLinkedData.name = name;
    scriptElement.textContent = JSON.stringify(parsedJsonLinkedData);
  } catch {
    return;
  }
}

function resolveDocumentHeadLanguage(language: string): DocumentHeadLanguage {
  const normalizedLanguage = language.split('-')[0];

  if (isDocumentHeadLanguage(normalizedLanguage)) {
    return normalizedLanguage;
  }

  return AppLanguageLocales.IT;
}

function writeDocumentHead(): void {
  const languageHead = documentHeadByLanguage[appliedLanguage];
  const countryHead = documentHeadByCountry[appliedCountryCode];
  const brandName = countryHead.brandName;
  const title = `${brandName} • ${languageHead.titleSuffix}`;
  const description = languageHead.descriptionTemplate.replaceAll(BRAND_NAME_PLACEHOLDER, brandName);

  document.documentElement.lang = languageHead.htmlLang;
  document.title = title;

  setMetaContentByName('description', description);
  setMetaContentByProperty('og:locale', languageHead.ogLocale);
  setMetaContentByProperty('og:site_name', brandName);
  setMetaContentByProperty('og:title', title);
  setMetaContentByProperty('og:description', description);
  setMetaContentByProperty('og:url', countryHead.ogUrl);
  setLinkHrefByRelationship('icon', countryHead.faviconHref);
  applyJsonLinkedData(countryHead.jsonLdUrl, brandName);
}

function applyDocumentHead(language: string): void {
  appliedLanguage = resolveDocumentHeadLanguage(language);
  writeDocumentHead();
}

export function applyDocumentCountryHead(countryCode: UserCountryCode | null): void {
  appliedCountryCode = resolveUserCountryCode(countryCode);
  writeDocumentHead();
}

export function bindDocumentHeadToI18n(i18nInstance: I18nInstance): void {
  applyDocumentHead(i18nInstance.resolvedLanguage ?? i18nInstance.language);
  i18nInstance.on('languageChanged', applyDocumentHead);
}
